import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  ArrowLeftRight,
  Clock,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  MessageSquare,
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-600'
};

const DUTY_LABELS = {
  front_desk: 'Front Desk',
  coaching: 'Coaching',
  academy_management: 'Academy Management'
};

const AdminShiftSwaps = () => {
  const [loading, setLoading] = useState(true);
  const [swapRequests, setSwapRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [reviewDialog, setReviewDialog] = useState(false);
  const [selectedSwap, setSelectedSwap] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchSwaps();
  }, []);

  const fetchSwaps = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/shift-swaps');
      setSwapRequests(response.data);
    } catch (error) {
      console.error('Error fetching swaps:', error);
      toast.error('Failed to load shift swap requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (status) => {
    if (!selectedSwap) return;

    try {
      await api.put(`/admin/shift-swaps/${selectedSwap.id}/review`, null, {
        params: {
          status: status,
          admin_notes: adminNotes || null
        }
      });
      toast.success(`Swap request ${status}`);
      setReviewDialog(false);
      setSelectedSwap(null);
      setAdminNotes('');
      fetchSwaps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    }
  };

  const openReviewDialog = (swap) => {
    setSelectedSwap(swap);
    setAdminNotes('');
    setReviewDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const filteredSwaps = swapRequests.filter(swap => {
    if (activeTab === 'pending') return swap.status === 'pending';
    if (activeTab === 'approved') return swap.status === 'approved';
    if (activeTab === 'rejected') return swap.status === 'rejected';
    return true;
  });

  const pendingCount = swapRequests.filter(s => s.status === 'pending').length;

  if (loading) {
    return (
      <DashboardLayout title="Shift Swap Requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Shift Swap Requests">
      <div className="space-y-6" data-testid="admin-shift-swaps-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <p className="text-slate-600">Review and manage staff shift swap requests</p>
          {pendingCount > 0 && (
            <Badge className="bg-amber-100 text-amber-700">
              {pendingCount} Pending
            </Badge>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending {pendingCount > 0 && `(${pendingCount})`}
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {filteredSwaps.length === 0 ? (
              <Card className="border border-slate-200">
                <CardContent className="p-8 lg:p-12 text-center">
                  <ArrowLeftRight className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    No {activeTab !== 'all' ? activeTab : ''} Swap Requests
                  </h3>
                  <p className="text-slate-500">
                    {activeTab === 'pending' 
                      ? 'No shift swap requests awaiting your review.'
                      : `No ${activeTab} swap requests found.`
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredSwaps.map((swap) => (
                  <Card key={swap.id} className="border border-slate-200" data-testid={`swap-${swap.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Badge className={STATUS_COLORS[swap.status]}>
                              {swap.status.charAt(0).toUpperCase() + swap.status.slice(1)}
                            </Badge>
                            {swap.duty_type && (
                              <span className="text-sm text-slate-500">
                                {DUTY_LABELS[swap.duty_type]}
                              </span>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Requester */}
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500 mb-1">Requested By</p>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-slate-400" />
                                <span className="font-medium">{swap.requester_name}</span>
                              </div>
                            </div>

                            {/* Shift Details */}
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-xs text-slate-500 mb-1">Shift</p>
                              <div className="flex items-center gap-2 mb-1">
                                <Calendar className="h-4 w-4 text-slate-400" />
                                <span className="font-medium">{formatDate(swap.shift_date)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-slate-400" />
                                <span className="text-sm">
                                  {formatTime(swap.shift_start_time)} - {formatTime(swap.shift_end_time)}
                                </span>
                              </div>
                            </div>

                            {/* Swap Partner */}
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-xs text-blue-600 mb-1">Swap With</p>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-blue-400" />
                                <span className="font-medium text-blue-700">{swap.target_staff_name}</span>
                              </div>
                            </div>
                          </div>

                          {swap.reason && (
                            <p className="text-sm text-slate-600 flex items-start gap-2">
                              <MessageSquare className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                              <span><strong>Reason:</strong> {swap.reason}</span>
                            </p>
                          )}

                          {swap.admin_notes && swap.status !== 'pending' && (
                            <p className="text-sm text-slate-500 mt-2 italic">
                              Admin notes: {swap.admin_notes}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        {swap.status === 'pending' && (
                          <div className="flex gap-2 ml-4">
                            <Button
                              size="sm"
                              onClick={() => openReviewDialog(swap)}
                              className="bg-blue-500 hover:bg-blue-600"
                              data-testid={`review-swap-${swap.id}`}
                            >
                              Review
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Review Dialog */}
        <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Review Swap Request</DialogTitle>
            </DialogHeader>

            {selectedSwap && (
              <div className="space-y-4 mt-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Requester</p>
                      <p className="font-medium">{selectedSwap.requester_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Swap With</p>
                      <p className="font-medium text-blue-600">{selectedSwap.target_staff_name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Shift Date</p>
                      <p className="font-medium">{formatDate(selectedSwap.shift_date)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Time</p>
                      <p className="font-medium">
                        {formatTime(selectedSwap.shift_start_time)} - {formatTime(selectedSwap.shift_end_time)}
                      </p>
                    </div>
                  </div>
                  {selectedSwap.reason && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-slate-500 text-sm">Reason</p>
                      <p className="text-sm">{selectedSwap.reason}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Admin Notes (Optional)</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add any notes about this decision..."
                    rows={3}
                    data-testid="admin-notes-input"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="mt-6 gap-2">
              <Button variant="outline" onClick={() => setReviewDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={() => handleReview('rejected')}
                data-testid="reject-swap-btn"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
              <Button 
                onClick={() => handleReview('approved')}
                className="bg-green-600 hover:bg-green-700"
                data-testid="approve-swap-btn"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminShiftSwaps;
